import React, { useState } from "react";
import { describeRaiseOutcome, photoCountLabel } from "../api/shape.js";
import { PhoneChip, PhoneFullScreen, PhoneSheet, Svg, ICON } from "./PhoneShell.jsx";

/** The phone words for an NCR's state (the desktop "How an NCR moves" chips). */
export function phoneStateLabel(row) {
  if (row.status === "Recorded" && row.awaitingResubmit) return "Returned to inspector";
  if (row.status === "Open") return "Raised to yard";
  return row.status;
}

const STATE_TONE = {
  Recorded: "warn",
  "Returned to inspector": "warn",
  "Raised to yard": "acc",
  Answered: "ok",
  Verified: "ok",
  Closed: "mute",
  Cancelled: "mute",
};

/** The second meta line of an NCR row: location, date, photos, inspector. */
export function ncrRowMeta(row) {
  return [row.location, row.dateLabel, photoCountLabel(row.photoCount), row.who].filter(Boolean).join(" · ");
}

function NcrRow({ row, selectable, selected, onToggle, onOpen }) {
  const label = phoneStateLabel(row);
  return (
    <div className={`ph-ncrrow${selectable && selected ? " on" : ""}`} data-testid="ph-ncr-row">
      {selectable ? (
        <label className="ph-ncrcheck">
          <input type="checkbox" checked={selected} onChange={() => onToggle(row.key)} aria-label={`Select ${row.subId}`} />
        </label>
      ) : null}
      <button type="button" className="ph-ncrbody" onClick={() => onOpen(row.key)}>
        <span className="hd">
          <span>{row.code} · {row.subId}</span>
          <span className={`ph-state-${STATE_TONE[label] || "mute"}`}>{label}</span>
        </span>
        <span className="t">{row.title}</span>
        <span className="m">{ncrRowMeta(row)}</span>
      </button>
    </div>
  );
}

function ReturnHistory({ active, history }) {
  if (!(active.awaitingResubmit || active.returnNote)) {
    return <span className="ph-faint" style={{ fontSize: 13 }}>Not returned before.</span>;
  }
  if (history === null || history === undefined) return <span className="ph-faint" style={{ fontSize: 13 }}>Loading…</span>;
  if (history.length === 0) return <span className="ph-note">{active.returnNote}</span>;
  return history.map((h) => (
    <div key={h.returnId} className="ph-versionrow" style={{ flexDirection: "column" }}>
      <span style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <span className="ph-muted">{h.returnedByUpn}</span>
        <PhoneChip tone={h.status === "Open" ? "warn" : "mute"}>{h.status}</PhoneChip>
      </span>
      <span>{h.returnNote}</span>
    </div>
  ));
}

/** NcrRaise.dc.html: the raise confirmation, then the result, as one bottom sheet. */
function RaiseSheet({ ctx, onClose }) {
  const { selectedRows, previewByCode, raiseResult, pending, handleRaise, handleResolve, blockingCodes, previewErrorText, refuseNotice } = ctx;
  const codes = [...new Set(selectedRows.map((r) => r.code))];
  const photoTotal = selectedRows.reduce((n, r) => n + (r.photoCount || 0), 0);
  if (raiseResult) {
    const allSent = raiseResult.projects.every((p) => p.status === "sent" || p.status === "duplicate");
    return (
      <PhoneSheet labelledBy="ph-raise-title" onClose={onClose} testId="ph-raise-sheet">
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span style={{ color: allSent ? "var(--ph-ok)" : "var(--ph-warn)" }}><Svg size={28} stroke={2.2}>{allSent ? ICON.checkCircle : ICON.warn}</Svg></span>
          <span className="ph-stack">
            <span id="ph-raise-title" style={{ fontSize: 20, fontWeight: 700 }}>{allSent ? "Raised to yard" : "Not everything was raised"}</span>
            {allSent ? (
              <span className="ph-note" style={{ fontSize: 14 }}>
                Mailed to the yard. {raiseResult.projects.length === 1 ? "It now waits" : "They now wait"} under “Raised, awaiting yard”.
              </span>
            ) : null}
          </span>
        </div>
        <div className="ph-stack" data-testid="ncr-raise-outcomes" style={{ gap: 8 }}>
          {raiseResult.projects.map((project) => (
            <div key={project.code} className="ph-historyrow">
              <span>{describeRaiseOutcome(project)}</span>
              {project.status === "unknown" ? (
                <span className="ph-two" style={{ marginTop: 6 }}>
                  <button type="button" className="ph-btn sec" disabled={pending === `resolve-${project.runId}`} onClick={() => handleResolve(project, "sent")}>Resolve: sent</button>
                  <button type="button" className="ph-btn" disabled={pending === `resolve-${project.runId}`} onClick={() => handleResolve(project, "not_sent")}>Resolve: not sent</button>
                </span>
              ) : null}
            </div>
          ))}
        </div>
        <button type="button" className="ph-btn pri" onClick={onClose}>Back to NCRs</button>
      </PhoneSheet>
    );
  }
  return (
    <PhoneSheet labelledBy="ph-raise-title" onClose={onClose} testId="ph-raise-sheet">
      <h2 id="ph-raise-title" className="ph-sheet-title">Raise {selectedRows.length} NCR{selectedRows.length === 1 ? "" : "s"} to the yard?</h2>
      <div className="ph-stack" style={{ gap: 8 }}>
        {selectedRows.map((row) => (
          <div key={row.key} className="ph-historyrow" style={{ background: "var(--ph-bg)" }}>
            <span className="ph-mono ph-small">{row.code} · {row.subId}</span>
            <span className="ph-cardtitle">{row.title}</span>
          </div>
        ))}
      </div>
      <dl className="ph-dl" style={{ gridTemplateColumns: "90px 1fr" }}>
        <dt>Sent to</dt>
        <dd>
          {codes.map((code) => {
            const p = previewByCode[code];
            return (
              <span key={code} style={{ display: "block" }}>
                {p && p.status === "ready" ? `${code}: ${p.recipients.to.join(", ")}` : `${code}: yard contact`}
              </span>
            );
          })}
        </dd>
        <dt>Includes</dt>
        <dd>Wording{photoTotal ? ` and ${photoTotal} photo${photoTotal === 1 ? "" : "s"}` : ""}</dd>
      </dl>
      <p className="ph-note">The mail goes out at once and cannot be recalled.</p>
      {blockingCodes.length > 0 ? (
        <div role="alert" data-testid="ncr-preview-blocking" className="ph-card badbox">
          {blockingCodes.map((code) => (
            <span key={code} style={{ color: "var(--ph-bad-ink)" }}>{code}: Cannot raise &mdash; {previewErrorText(code)}</span>
          ))}
        </div>
      ) : null}
      {refuseNotice ? <span data-testid="ncr-refuse-notice" style={{ color: "var(--ph-bad)", fontSize: 13 }}>{refuseNotice}</span> : null}
      <button type="button" className="ph-btn pri" disabled={pending === "raise" || blockingCodes.length > 0} onClick={handleRaise}>
        {pending === "raise" ? "…" : "Raise to yard"}
      </button>
      <button type="button" className="ph-btn link" onClick={onClose}>Cancel</button>
    </PhoneSheet>
  );
}

/** NcrReturn.dc.html: return to the inspector with the required note and the return history (and the same sheet for Dismiss). */
function NoteSheet({ ctx, onClose }) {
  const { noteDraft, setNoteDraft, confirmNote, pending, active, activeHistory } = ctx;
  const isReturn = noteDraft.action === "return";
  const ok = noteDraft.text.trim().length > 0;
  return (
    <PhoneSheet labelledBy="ph-note-title" onClose={onClose} testId="ph-note-sheet">
      <div className="ph-stack" style={{ gap: 4 }}>
        <h2 id="ph-note-title" className="ph-sheet-title">{isReturn ? "Return to inspector" : "Dismiss"}</h2>
        <span className="ph-sheet-note">
          {isReturn
            ? `${active.subId} goes back to the inspector's capture app with your note. Nothing is sent to the yard.`
            : `${active.subId} is cancelled with your note. Nothing is sent to the yard.`}
        </span>
      </div>
      <label className="ph-field">
        Note (required)
        <textarea
          className="ph-textarea"
          rows={4}
          data-autofocus="true"
          value={noteDraft.text}
          placeholder={isReturn ? "What should the inspector add or change?" : "Why is this NCR dismissed?"}
          onChange={(e) => setNoteDraft({ ...noteDraft, text: e.target.value })}
        />
      </label>
      {isReturn ? (
        <section className="ph-section" data-testid="ncr-return-history">
          <h3 className="ph-label">Return history</h3>
          <ReturnHistory active={active} history={activeHistory} />
        </section>
      ) : null}
      <button type="button" className={`ph-btn pri${isReturn ? " amber" : ""}`} disabled={!ok || pending === noteDraft.action} onClick={confirmNote}>
        {isReturn ? "Return to inspector" : "Dismiss"}
      </button>
      <button type="button" className="ph-btn link" onClick={onClose}>Cancel</button>
    </PhoneSheet>
  );
}

/** NcrDetail.dc.html: Wording / Yard mail preview for one NCR. */
function NcrDetail({ ctx, onBack, onRaise, onReturn, onDismiss }) {
  const { active, tab, wordingValue, setWordingDraft, saveActiveDescription, activePreview, previewPending, previewErrorText } = ctx;
  const [view, setView] = useState("wording");
  const label = phoneStateLabel(active);
  const canDecide = tab === "toDecide" && active.status === "Recorded";
  const wordingLocked = active.status !== "Recorded" || Boolean(active.raiseRunId);
  const photos = photoCountLabel(active.photoCount);
  return (
    <PhoneFullScreen
      title={`${active.code} · ${active.subId}`}
      titleId="ph-ncr-detail-title"
      onBack={onBack}
      backLabel="Back to NCRs"
      testId="ph-ncr-detail"
      footerTwo
      headerRight={<PhoneChip tone={STATE_TONE[label] || "mute"} className={`ph-chip big ${STATE_TONE[label] || "mute"}`}>{label}</PhoneChip>}
      footer={
        canDecide ? (
          <>
            <button type="button" className="ph-btn mid" onClick={onReturn}>Return to inspector</button>
            <button type="button" className="ph-btn pri" style={{ minHeight: 48, fontSize: 15 }} onClick={onRaise}>Raise to yard</button>
          </>
        ) : null
      }
    >
      <div role="tablist" aria-label="View" className="ph-segment" style={{ margin: "-6px 0 0" }}>
        <button type="button" role="tab" aria-selected={view === "wording"} className={view === "wording" ? "on" : ""} onClick={() => setView("wording")}>Wording</button>
        <button type="button" role="tab" aria-selected={view === "mail"} className={view === "mail" ? "on" : ""} onClick={() => setView("mail")}>Yard mail preview</button>
      </div>
      {view === "wording" ? (
        <>
          <label className="ph-field">
            Title
            <input className="ph-input" type="text" value={active.title} readOnly />
          </label>
          <label className="ph-field" htmlFor="ncr-wording">NCR wording, editable before raising</label>
          <textarea
            id="ncr-wording"
            className="ph-textarea"
            rows={6}
            style={{ marginTop: -10 }}
            aria-label="NCR wording, editable before raising"
            value={wordingValue}
            disabled={wordingLocked}
            onChange={(e) => setWordingDraft({ key: active.key, text: e.target.value })}
            onBlur={(e) => saveActiveDescription(e.target.value)}
          />
          <dl className="ph-dl">
            {active.location ? (<><dt>Location</dt><dd>{active.location}</dd></>) : null}
            <dt>Recorded</dt>
            <dd>{[active.dateLabel, active.who && `by ${active.who}`].filter(Boolean).join(" ")}</dd>
            {photos ? (<><dt>Photos</dt><dd>{photos}</dd></>) : null}
          </dl>
          {active.returnNote ? (
            <section className="ph-section">
              <h3 className="ph-label">Return note</h3>
              <span className="ph-note">{active.returnNote}</span>
            </section>
          ) : null}
          {canDecide ? (
            <button type="button" className="ph-btn link" style={{ justifyContent: "flex-start", padding: 0 }} onClick={onDismiss}>Dismiss this NCR…</button>
          ) : null}
        </>
      ) : (
        <>
          <span className="ph-note">Mail text the yard receives</span>
          <div className="ph-mail" data-testid="ph-ncr-mail">
            {activePreview && activePreview.status === "ready" ? (
              <>
                <span className="hdr">
                  To: {activePreview.recipients.to.join(", ")}
                  {activePreview.recipients.cc && activePreview.recipients.cc.length ? <><br />Cc: {activePreview.recipients.cc.join(", ")}</> : null}
                  <br />Subject: {activePreview.subject}
                </span>
                {activePreview.coverText.split("\n").map((line, i) => (line.trim() ? <span key={i}>{line}</span> : null))}
              </>
            ) : activePreview && activePreview.status === "failed" ? (
              <span style={{ color: "var(--bad, #D00530)" }}>Cannot raise &mdash; {previewErrorText(active.code)}</span>
            ) : previewPending ? (
              <span>Loading…</span>
            ) : (
              <span>No preview for this NCR.</span>
            )}
          </div>
          <p className="ph-note">The covering text is fixed. Only the NCR wording and the recipient can change, and the recipient comes from the project&apos;s parties list.</p>
        </>
      )}
    </PhoneFullScreen>
  );
}

/**
 * NCRs on a phone (Ncr.dc.html, NcrDetail.dc.html, NcrRaise.dc.html,
 * NcrReturn.dc.html). All state and handlers belong to NCR.jsx (`ctx`): the
 * six state tabs, the selection, the editable wording, the raise preview, the
 * raise handler and its outcome text, the return/dismiss note and history.
 */
export default function PhoneNcr({ ctx }) {
  const { model, TABS, tab, selectTab, projectCounts, rows, selected, toggleSelect, active, setActiveKey, onExport, handleExport, exportCodes, exportResults, pending, describeExport, setRaiseResult, setNoteDraft, noteDraft, initialDetail, blockingCodes } = ctx;
  const [detailOpen, setDetailOpen] = useState(Boolean(initialDetail));
  const [sheet, setSheet] = useState(null); // "raise" | "note"
  const isDecide = tab === "toDecide";
  const n = selected.size;

  function openRow(key) {
    setActiveKey(key);
    setDetailOpen(true);
  }
  function openRaise() {
    setRaiseResult(null);
    setSheet("raise");
  }
  function closeRaise() {
    // After a raise the NCR has moved on; leave its detail too.
    if (ctx.raiseResult) setDetailOpen(false);
    setSheet(null);
    setRaiseResult(null);
  }
  function openNote(action) {
    setNoteDraft({ action, text: "" });
    setSheet("note");
  }
  function closeNote() {
    setNoteDraft(null);
    setSheet(null);
  }
  // Raise from the detail screen raises this one NCR: the selection becomes just this row, then the same raise handler runs.
  function raiseActive() {
    ctx.selectOnly(active.key);
    openRaise();
  }

  const noteCtx = { ...ctx, confirmNote: async () => { await ctx.confirmNote(); setSheet(null); setDetailOpen(false); } };

  return (
    <div className="ph-screen" data-testid="phone-ncr">
      <header className="ph-head ncr">
        <div className="ph-titlerow" style={{ padding: "0 20px" }}>
          <h1 className="ph-h1">NCRs</h1>
          {onExport ? (
            <button type="button" className="ph-smallbtn" data-testid="ncr-export" disabled={pending === "export" || exportCodes.length === 0} onClick={handleExport}>
              {pending === "export" ? "Exporting…" : "Export register"}
            </button>
          ) : null}
        </div>
        <div role="tablist" aria-label="NCR state" className="ph-chips">
          {TABS.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={`ph-chipbtn${tab === t.key ? " on" : ""}`} onClick={() => selectTab(t.key)}>
              {t.label}
              {projectCounts[t.key] ? ` · ${projectCounts[t.key]}` : ""}
            </button>
          ))}
        </div>
      </header>
      <main className="ph-main tight" style={{ paddingBottom: isDecide ? 96 : undefined }}>
        {isDecide ? <span className="ph-note">Tick NCRs to raise them to the yard together, or open one to edit its wording.</span> : null}
        {onExport && pending === "export" ? <span data-testid="ncr-export-progress" className="ph-note">Building the register package (register + photos)…</span> : null}
        {onExport && exportResults ? (
          <div className="ph-stack" data-testid="ncr-export-results" style={{ gap: 8 }}>
            {exportResults.map((result) => (
              <div key={result.code} className="ph-historyrow">
                <span style={result.status === "done" ? undefined : { color: "var(--ph-bad)" }}>{result.code}: {describeExport(result)}</span>
                {result.status === "done" && typeof result.fileUrl === "string" && /^https:\/\//.test(result.fileUrl) ? (
                  <a href={result.fileUrl} target="_blank" rel="noopener noreferrer">Open register</a>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
        {model.errors.length > 0 ? (
          <div className="ph-stack" data-testid="ncr-errors" style={{ gap: 8 }}>
            {model.errors.map((e) => (
              <div key={e.code} className="ph-card"><span className="ph-cardmeta"><PhoneChip tone="bad">!</PhoneChip>{e.code}</span><span style={{ color: "var(--ph-bad)" }}>Unavailable &mdash; {e.error}</span></div>
            ))}
          </div>
        ) : null}
        {rows.length === 0 ? (
          <div className="ph-card"><span className="ph-note">Nothing to do</span></div>
        ) : (
          rows.map((row) => (
            <NcrRow key={row.key} row={row} selectable={isDecide} selected={selected.has(row.key)} onToggle={toggleSelect} onOpen={openRow} />
          ))
        )}
      </main>

      {isDecide ? (
        <footer className="ph-stickybar">
          <button type="button" className="ph-btn pri" aria-disabled={n === 0 ? "true" : undefined} data-testid="ph-ncr-raise" onClick={() => (n > 0 ? openRaise() : null)}>
            {n ? `Raise ${n} to yard` : "Tick NCRs to raise"}
          </button>
        </footer>
      ) : null}

      {detailOpen && active ? (
        <NcrDetail ctx={ctx} onBack={() => setDetailOpen(false)} onRaise={raiseActive} onReturn={() => openNote("return")} onDismiss={() => openNote("dismiss")} />
      ) : null}
      {sheet === "raise" ? <RaiseSheet ctx={{ ...ctx, blockingCodes }} onClose={closeRaise} /> : null}
      {sheet === "note" && noteDraft && active ? <NoteSheet ctx={noteCtx} onClose={closeNote} /> : null}
    </div>
  );
}
